import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { IChildrenProp } from 'types/components/components.interface';

interface IAudioControls {
  play: () => void;
  pause: () => void;
}

const PlayerContext = createContext<{
  isTrackPlaying: boolean;
  isVideoPlaying: boolean;
  setIsTrackPlaying: Dispatch<SetStateAction<boolean>>;
  setIsVideoPlaying: Dispatch<SetStateAction<boolean>>;
  registerAudioControls: (controls: IAudioControls | null) => void;
  playTrack: () => void;
  pauseTrack: () => void;
} | null>(null);

export const PlayerProvider = ({ children }: IChildrenProp) => {
  const [isTrackPlaying, setIsTrackPlaying] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const audioControlsRef = useRef<IAudioControls | null>(null);

  const registerAudioControls = useCallback(
    (controls: IAudioControls | null) => {
      audioControlsRef.current = controls;
    },
    []
  );

  const playTrack = useCallback(() => {
    audioControlsRef.current?.play();
  }, []);

  const pauseTrack = useCallback(() => {
    audioControlsRef.current?.pause();
  }, []);

  return (
    <PlayerContext.Provider
      value={{
        isTrackPlaying,
        isVideoPlaying,
        setIsTrackPlaying,
        setIsVideoPlaying,
        registerAudioControls,
        playTrack,
        pauseTrack,
      }}>
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayerProvider = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayerProvider must be used within a PlayerProvider');
  }
  return context;
};
